/**
 * Modal-Wrapper für ZapPlayer.
 *
 * Baut .player-overlay mit Backdrop, Panel, Header (Titel + HLS-Badge +
 * Toolbar-Slot + Close), Stage (mit internem <video>) und Footer
 * (Shortcuts-Hint + URL-Feld). Toolbar und Settings-Panel füllt der
 * ZapPlayer von außen — das hier liefert nur das Skelett.
 *
 * CSS-Klassen sind in /opt/zap/frontend/src/styles/zap.css definiert.
 * Esc + Backdrop-Click triggern onClose(); wenn ein Help-Overlay offen ist,
 * lässt der Esc-Handler das Help zuerst zugreifen.
 */
import { icon } from '../../core/icons.js';

function escAttr(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}

// Footer kept tiny — full shortcut reference lives in the help-overlay
// (toolbar `?` button). The label below is just so the URL-field has
// context; everything else is one keystroke away.
const FOOTER_HINT_HTML = `
  <span class="dim" style="font-size:11px;">
    Tastatur-Hilfe: <kbd>?</kbd> &nbsp;·&nbsp; VLC/mpv-URL (Token ~1h):
  </span>`;

/**
 * @param {Object} opts
 * @param {string} [opts.name]      Display title in header
 * @param {boolean} [opts.hlsBadge] Show "HLS" badge next to title (default true)
 * @param {string} [opts.vlcUrl]    Full streaming URL for the footer input
 * @param {() => void} [opts.onClose]
 */
export function createModalShell({ name = '', hlsBadge = true, vlcUrl = '', onClose }) {
  // Plan v2 (2026-05-02): native HTML5 controls and the gear-button
  // overlay are gone — replaced by a single `.po-controls` custom bar as
  // sibling of <video> within the stage. That keeps the bar inside
  // `stage.requestFullscreen()` and unifies the look across browsers.
  // The `data-slot="toolbar"` placeholder is kept so inline-mode (which
  // still uses `createToolbar` from `ui/toolbar.js`) keeps working.
  const html = `
    <div class="player-overlay" role="dialog" aria-modal="true">
      <div class="player-overlay__backdrop" data-act="close"></div>
      <div class="player-overlay__panel">
        <header class="player-overlay__h">
          <span class="player-overlay__title" title="${escAttr(name)}">${escAttr(name)}</span>
          ${hlsBadge ? '<span class="player-overlay__hls-badge">HLS</span>' : ''}
          <div class="player-overlay__toolbar" data-slot="toolbar"></div>
          <button class="player-overlay__close" data-act="close" aria-label="Schließen">
            ${icon('x', { size: 16 })}
          </button>
        </header>
        <div class="player-overlay__stage" data-slot="stage">
          <video class="player-overlay__video" preload="metadata" playsinline></video>
          <div class="po-controls" data-slot="controls"></div>
        </div>
        <footer class="player-overlay__f" data-slot="footer">
          ${FOOTER_HINT_HTML}
          <input class="player-overlay__url mono" readonly value="${escAttr(vlcUrl)}"
                 onclick="this.select()" title="URL für mpv/VLC (mit Token, ~1h gültig)">
        </footer>
      </div>
    </div>`;

  const tmp = document.createElement('div');
  tmp.innerHTML = html.trim();
  const overlay = tmp.firstElementChild;
  document.body.appendChild(overlay);

  const panel        = overlay.querySelector('.player-overlay__panel');
  const header       = overlay.querySelector('.player-overlay__h');
  const stage        = overlay.querySelector('.player-overlay__stage');
  const footer       = overlay.querySelector('.player-overlay__f');
  const video        = overlay.querySelector('.player-overlay__video');
  const toolbar      = overlay.querySelector('[data-slot="toolbar"]');
  const controlsHost = overlay.querySelector('[data-slot="controls"]');
  const urlInput     = overlay.querySelector('.player-overlay__url');

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    onClose?.();
  };

  const onKey = (e) => {
    if (e.key !== 'Escape') return;
    // If a help overlay is open, let it handle Esc first; it will remove
    // itself, and a second Esc closes the modal.
    if (document.querySelector('.zap-help-overlay')) return;
    close();
  };

  overlay.addEventListener('click', (e) => {
    if (e.target.closest('[data-act="close"]')) close();
  });
  document.addEventListener('keydown', onKey);

  return { overlay, panel, header, stage, footer, video, toolbar, controlsHost, urlInput, close };
}
